// Pure helpers for the Site Progress timeline + compare (see backend
// common/site_progress.py for the source of truth on kinds).

export const KIND_LABEL = {
  orthomosaic: 'Orthomosaic',
  model_3d: '3D model',
  point_cloud: 'Point cloud',
  site_scan: 'Site scan',
  elevation: 'Elevation model',
  stills: 'Aerial stills',
  video: 'Flyover video',
  report: 'Progress report',
  other: 'File',
};

export function compareFrames(visits, kind) {
  const out = [];
  (visits || []).forEach((v) => {
    const d = (v.deliverables || []).find((x) => x.kind === kind && x.comparable);
    if (d && v.date) out.push({ visitId: v.id, date: v.date, src: d.preview_url, url: d.url, label: d.label });
  });
  out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return out;
}

// Default pair = oldest vs newest; keeps a valid user pick when the list changes.
export function defaultPair(frames, a, b) {
  const ids = frames.map((f) => f.visitId);
  const left = ids.includes(a) ? a : ids[0];
  let right = ids.includes(b) && b !== left ? b : ids[ids.length - 1];
  if (right === left) right = ids.find((i) => i !== left) || right;
  return [left, right];
}

export function daysApart(a, b) {
  if (!a || !b) return null;
  const ms = Math.abs(new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`));
  return Math.round(ms / 86400000);
}

export function summaryLine(summary) {
  if (!summary || !summary.visits) return '';
  const parts = [`${summary.visits} visit${summary.visits === 1 ? '' : 's'}`, `${summary.delivered} delivered`];
  if (summary.first_date && summary.last_date && summary.first_date !== summary.last_date) {
    parts.push(`${summary.first_date} to ${summary.last_date}`);
  } else if (summary.last_date) {
    parts.push(summary.last_date);
  }
  return parts.join(' · ');
}

export function clampSplit(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 50;
  return Math.min(100, Math.max(0, Math.round(n)));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parts(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return null;
  const mo = Number(m[2]) - 1;
  if (mo < 0 || mo > 11) return null;
  return { y: Number(m[1]), m: mo, d: Number(m[3]) };
}

// "Oct 10, 2026" from a visit's calendar date ("2026-10-10"); no time-zone shift.
export function humanDate(iso) {
  const p = parts(iso);
  return p ? `${MONTHS[p.m]} ${p.d}, ${p.y}` : '';
}

// "Oct 10" (year dropped).
export function shortHuman(iso) {
  const p = parts(iso);
  return p ? `${MONTHS[p.m]} ${p.d}` : '';
}

// "Sep 12 – Oct 10, 2026", "Dec 2, 2025 – Jan 9, 2026", or one date.
export function rangeLine(a, b) {
  const pa = parts(a);
  const pb = parts(b);
  if (!pa && !pb) return '';
  if (!pa || !pb || a === b) return humanDate(pa ? a : b);
  return pa.y === pb.y ? `${shortHuman(a)} – ${humanDate(b)}` : `${humanDate(a)} – ${humanDate(b)}`;
}

// "Oct 10 vs Sep 12 · 28 days" — later date first.
export function vsLine(a, b) {
  if (!a || !b) return '';
  const [early, late] = a <= b ? [a, b] : [b, a];
  const n = daysApart(early, late);
  return `${shortHuman(late)} vs ${shortHuman(early)}${n !== null ? ` · ${n} day${n === 1 ? '' : 's'}` : ''}`;
}

// "3 visits · 2 delivered · Sep 12 – Oct 10, 2026" in human dates.
export function headerLine(summary) {
  if (!summary || !summary.visits) return '';
  const out = [`${summary.visits} visit${summary.visits === 1 ? '' : 's'}`, `${summary.delivered} delivered`];
  const r = rangeLine(summary.first_date, summary.last_date);
  if (r) out.push(r);
  return out.join(' · ');
}

// One status per visit for the pill. A visit whose walk-through video is
// waiting on processing reads "Processing", never "Delivered".
export function visitStatus(v) {
  const ds = (v && v.deliverables) || [];
  if (v && v.status === 'failed') return { key: 'failed', label: 'Not dispatched' };
  if (v && v.status === 'completed') return { key: 'delivered', label: 'Delivered' };
  if (ds.some((d) => d.scan && d.scan.status === 'queued')) return { key: 'processing', label: 'Processing' };
  if (ds.length) return { key: 'delivered', label: 'Delivered' };
  return { key: 'scheduled', label: 'Scheduled' };
}

export const CAPTURE_LABEL = { mapped: 'Mapped capture', visual: 'Visual progress visit' };

// Latest deliverable that opens in the browser viewer (newest visit first).
export function latestViewable(visits) {
  let best = null;
  (visits || []).forEach((v) => {
    (v.deliverables || []).forEach((d) => {
      if (!d.view || !v.date) return;
      const rank = d.kind === 'site_scan' ? 1 : 0;
      if (!best || v.date > best.date || (v.date === best.date && rank > best.rank)) {
        best = { date: v.date, rank, item: { url: d.url, format: d.view.format, kind: d.kind, date: v.date, label: d.label, id: d.id || d.url } };
      }
    });
  });
  return best ? best.item : null;
}

const IMAGE_EXT = /(\.(jpe?g|png|webp|avif|gif)(\?|#|$))|^data:image\//i;
const VIDEO_EXT = /(\.(mp4|m4v|webm|mov)(\?|#|$))|^data:video\//i;

// A visit's photos and flyover videos that play in the page. Stills need a
// picture (a preview, or the file is an image); videos need a direct video
// file. Everything else stays a plain link.
export function galleryItems(visit) {
  const out = [];
  const when = humanDate(visit && visit.date);
  ((visit && visit.deliverables) || []).forEach((d, i) => {
    if (d.scan) return;
    const id = d.id || `${visit.id}-${i}`;
    if (d.kind === 'stills') {
      const src = d.preview_url || (IMAGE_EXT.test(d.url || '') ? d.url : null);
      if (src) out.push({ id, type: 'image', src, url: d.url, alt: [d.label || 'Aerial still', when].filter(Boolean).join(', ') });
    } else if (d.kind === 'video' && VIDEO_EXT.test(d.url || '')) {
      out.push({ id, type: 'video', src: d.url, url: d.url, poster: d.preview_url || null, alt: [d.label || 'Flyover video', when].filter(Boolean).join(', ') });
    }
  });
  return out;
}

export function galleryCount(items) {
  const p = items.filter((x) => x.type === 'image').length;
  const v = items.length - p;
  return [p ? `${p} photo${p === 1 ? '' : 's'}` : '', v ? `${v} video${v === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
}

// Next index for prev/next with wrap-around.
export function stepIndex(i, d, n) {
  if (!n) return 0;
  return (((i + d) % n) + n) % n;
}
